import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IoSparklesOutline,
  IoSendOutline,
  IoTrashOutline,
  IoAddOutline,
  IoConstructOutline,
} from 'react-icons/io5';
import { api } from '../api';

interface UiMessage {
  id?: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  toolName?: string | null;
  pending?: boolean;
}

const EXAMPLES = [
  'Какие товары закончатся в ближайшую неделю?',
  'Топ-10 продаж за последний месяц',
  'Есть ли товары с высоким риском возврата?',
  'Сколько отзывов с оценкой 1-2 ждут ответа?',
  'Какие акции Ozon стоит игнорировать?',
];

export function ChatPage() {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Поддержка ?msg=... — заполняем поле из URL при загрузке
  useEffect(() => {
    const msg = params.get('msg');
    if (msg) {
      setInput(msg);
      const next = new URLSearchParams(params);
      next.delete('msg');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convs = useQuery({ queryKey: ['chat-convs'], queryFn: api.chatConversations });

  const detail = useQuery({
    queryKey: ['chat-conv', conversationId],
    queryFn: () => api.chatConversation(conversationId!),
    enabled: !!conversationId,
  });

  // Когда переключаем диалог — загружаем его сообщения
  useEffect(() => {
    if (detail.data) {
      setMessages(
        detail.data.messages.map((m) => ({
          id: m.id,
          role: m.role as UiMessage['role'],
          content: m.content,
          toolName: m.toolName,
        })),
      );
    }
  }, [detail.data]);

  // Автоскролл вниз при новых сообщениях
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = useMutation({
    mutationFn: api.chatSend,
    onSuccess: (res) => {
      setConversationId(res.conversationId);
      // Подменяем последний pending на ответ ассистента
      setMessages((prev) => [
        ...prev.filter((m) => !m.pending),
        { role: 'assistant', content: res.assistant },
      ]);
      qc.invalidateQueries({ queryKey: ['chat-convs'] });
      if (conversationId) qc.invalidateQueries({ queryKey: ['chat-conv', conversationId] });
    },
    onError: (e) => {
      setMessages((prev) => [
        ...prev.filter((m) => !m.pending),
        { role: 'assistant', content: `Ошибка: ${(e as Error).message}` },
      ]);
    },
  });

  const del = useMutation({
    mutationFn: api.chatDelete,
    onSuccess: () => {
      setConversationId(null);
      setMessages([]);
      qc.invalidateQueries({ queryKey: ['chat-convs'] });
    },
  });

  function handleSend(text?: string) {
    const msg = (text ?? input).trim();
    if (!msg || send.isPending) return;
    setInput('');
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: msg },
      { role: 'assistant', content: 'Думаю…', pending: true },
    ]);
    send.mutate({ conversationId: conversationId ?? undefined, message: msg });
  }

  function newChat() {
    setConversationId(null);
    setMessages([]);
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h2><IoSparklesOutline style={{ verticalAlign: 'middle' }} /> AI-ассистент</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Чат над данными: продажи, остатки, возвраты, отзывы, акции. Доступ через tool calling.
          </div>
        </div>
      </div>

      <div className="chat-layout">
        <aside className="chat-sidebar">
          <button className="btn primary" style={{ width: '100%', marginBottom: 12 }} onClick={newChat}>
            <IoAddOutline /> Новый диалог
          </button>
          <div className="chat-conv-list">
            {convs.data?.items.map((c) => (
              <div
                key={c.id}
                className={`chat-conv-item${conversationId === c.id ? ' active' : ''}`}
                onClick={() => setConversationId(c.id)}
              >
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.title}
                </div>
                <button
                  className="chat-conv-delete"
                  onClick={(e) => { e.stopPropagation(); if (confirm('Удалить диалог?')) del.mutate(c.id); }}
                >
                  <IoTrashOutline />
                </button>
              </div>
            ))}
            {convs.data?.items.length === 0 && (
              <div className="muted" style={{ fontSize: 12, padding: '8px 4px' }}>Ещё нет диалогов</div>
            )}
          </div>
        </aside>

        <main className="chat-main">
          <div className="chat-messages" ref={scrollRef}>
            {messages.length === 0 && !detail.isLoading && (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <IoSparklesOutline style={{ fontSize: 48, color: 'var(--text-muted)' }} />
                <h3 style={{ marginTop: 16 }}>Спросите что-нибудь</h3>
                <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
                  Ассистент знает о ваших товарах, продажах, возвратах и отзывах. Примеры:
                </div>
                <div style={{ marginTop: 24, display: 'grid', gap: 8, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}>
                  {EXAMPLES.map((ex) => (
                    <button key={ex} className="chat-example" onClick={() => handleSend(ex)}>{ex}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.filter((m) => m.role !== 'tool').map((m, i) => (
              <div key={i} className={`chat-msg chat-msg-${m.role}`}>
                <div className="chat-msg-role">
                  {m.role === 'user' ? 'Вы' : <><IoSparklesOutline /> Ассистент</>}
                </div>
                <div className="chat-msg-body">
                  {m.pending ? <em className="muted">{m.content}</em> : m.content.split('\n').map((line, j) => (
                    <div key={j}>{line || ' '}</div>
                  ))}
                </div>
              </div>
            ))}
            {/* Опционально: показать использованные tools (compact) */}
            {messages.some((m) => m.role === 'tool') && (
              <div className="muted" style={{ fontSize: 11, padding: '4px 12px' }}>
                <IoConstructOutline style={{ verticalAlign: 'middle' }} /> использовал tools: {Array.from(new Set(messages.filter((m) => m.role === 'tool').map((m) => m.toolName))).join(', ')}
              </div>
            )}
          </div>

          <div className="chat-input-row">
            <textarea
              className="chat-input"
              placeholder="Спросите что-нибудь…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              rows={2}
              disabled={send.isPending}
            />
            <button className="btn primary" onClick={() => handleSend()} disabled={send.isPending || !input.trim()}>
              <IoSendOutline /> Отправить
            </button>
          </div>
        </main>
      </div>
    </>
  );
}
